import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlatformConnectionButtons } from '@/components/platform/PlatformConnectionButtons';
import { ConnectedPlatformsList } from '@/components/platform/ConnectedPlatformsList';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';
import { AuthGuard } from '@/components/common/AuthGuard';
import { AutoAccountCleanup } from '@/components/platform/AutoAccountCleanup';

export default function PlatformConnections() {
  const { accounts, loading, refreshAccounts, disconnectAccount } = useConnectedAccounts();

  const connectedPlatformIds = accounts?.map(account => account.platform) || [];

  const handleConnectionSuccess = () => {
    refreshAccounts();
  };

  const handleDisconnect = async (accountId: string, platform: string) => {
    await disconnectAccount(accountId, platform);
  };

  const handleConfigure = (accountId: string, platform: string) => {
    // Navigate to platform configuration page
    window.location.href = `/platform-config?platform=${platform}&account=${accountId}`;
  };

  const handleView = (accountId: string, platform: string) => {
    // Navigate to platform-specific feed or dashboard
    window.location.href = `/dashboard-sidebar/platforms?view=${platform}&account=${accountId}`;
  };

  return (
    <AuthGuard>
      <AutoAccountCleanup />
      <div className="container mx-auto py-8 space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Platform Connections</h1>
          <p className="text-muted-foreground">
            Connect and manage your social media accounts
          </p>
        </div>

        <Tabs defaultValue="connect" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="connect">Connect Platforms</TabsTrigger>
            <TabsTrigger value="manage">Manage Connected</TabsTrigger>
          </TabsList>

          <TabsContent value="connect" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Available Platforms</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Connect your social media accounts to start monitoring and managing your content
                </p>
              </CardHeader>
              <CardContent>
                <PlatformConnectionButtons
                  connectedPlatforms={connectedPlatformIds}
                  onConnectionSuccess={handleConnectionSuccess}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="manage" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Connected Accounts</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Manage your connected social media accounts
                </p>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Loading connected accounts...</p>
                  </div>
                ) : (
                  <ConnectedPlatformsList
                    accounts={accounts || []}
                    onDisconnect={handleDisconnect}
                    onConfigure={handleConfigure}
                    onView={handleView}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AuthGuard>
  );
}